import React, { useState, useEffect, useMemo } from "react";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import axios from "axios";
import {
  Paper,
  Button,
  Breadcrumbs,
  Typography,
  TextField,
  Container,
  Grid,
  Box,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTheme } from "@emotion/react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GetAppIcon from "@mui/icons-material/GetApp";
import * as XLSX from "xlsx";
import { Pagination, TablePagination } from "@mui/material";
import { BASE_URL } from "../../urlConfig/Url";

const RepAssetPhStockTake = () => {
  const theme = useTheme();
  const [barcode, setBarcode] = useState("");
  const [plantCode, setPlantCode] = useState("");
  const [assetCode, setAssetCode] = useState("");
  const [category, setCategory] = useState("");
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [remark, setRemark] = useState("");
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [cName, setCName] = useState({ plantCodes: [], assetCodes: [] });
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    fetchPlantCodes();
  }, []);

  const fetchPlantCodes = async () => {
    try {
      const response = await axios.post(
        BASE_URL + "/api/transaction/loadComboPlantAndAsset"
      );
      if (response.data.success) {
        setCName(response.data.response);
      }
    } catch (error) {
      toast.error("Error fetching plant codes:", error);
    }
  };

  const fetchAssetCodes = async (selectedPlantCode) => {
    try {
      const response = await axios.post(
        BASE_URL + "/api/transaction/searchByPlantCode",
        { plantCode: selectedPlantCode }
      );
      if (response.data.success) {
        setCName((prev) => ({
          ...prev,
          assetCodes: response.data.response.map((item) => item.AssetCode),
        }));
      }
    } catch (error) {
      toast.error("Error fetching asset codes:", error);
    }
  };

  const handlePlantCodeChange = (e) => {
    const selectedPlantCode = e.target.value;
    setPlantCode(selectedPlantCode);
    setAssetCode(""); // Reset asset code when plant code changes
    fetchAssetCodes(selectedPlantCode);
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        BASE_URL + "/api/report/getAssetReportstockTake",
        {
          plantCode: plantCode,
          assetCode: assetCode,
          //   customerName: customerName,
          category: category,
          remark: remark,
          fromDate: fromDate,
          toDate: toDate,
        }
      );
      if (response.data.success) {
        setSearchResults(response.data.response);
        if (response.data.response.length === 0) {
          toast.info("No data found");
        }
      } else {
        toast.error("Failed to fetch asset identification data");
      }
    } catch (error) {
      toast.error("Error fetching asset identification data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setBarcode("");
    setPlantCode("");
    setAssetCode("");
    setCategory("");
    setFromDate(new Date().toISOString().split("T")[0]);
    setToDate(new Date().toISOString().split("T")[0]);
    setSearchResults([]);
  };

  const exportToExcel = () => {
    const dataToExport = searchResults.map((row) => ({
      // Barcode: row.Barcode,
      PlantCode: row.PlantCode,
      AssetCode: row.AssetCode,
      Description: row.Description,
      Category: row.Category,
      Depreciation: row.Depreciation,
      DepreciationYear: row.DepreciationYear,
      AfterDepreciation: row.AfterDepreciation,
      // SrNo: row.SrNo,
      CreatedBy: row.CreatedBy,
      // FromPlant: row.FromPlant,
      // De_AssignedBy: row.De_AssignedBy,
      // AssignStatus: row.AssignStatus,
      CreatedDate: new Date(row.CreatedDate).toLocaleString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AssetIdentification");
    XLSX.writeFile(workbook, "asset_identification_report.xlsx");
  };

  const handleSearchforasds = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredResults = useMemo(() => {
    return searchResults.filter((row) =>
      Object.values(row).some(
        (value) =>
          value &&
          value
            .toString()
            .toLowerCase()
            .trim()
            .includes(searchTerm.trim().toLowerCase())
      )
    );
  }, [searchResults, searchTerm]);

  const sortedResults = useMemo(() => {
    let sortableItems = [...filteredResults];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredResults, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const SortableHeader = ({ column }) => (
    <th onClick={() => requestSort(column)} style={{ cursor: "pointer" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ mr: 1, textTransform: "uppercase", letterSpacing: "0.5px" }}
        >
          {column}
        </Typography>
        <Box sx={{ width: 18, display: "flex", justifyContent: "center" }}>
          {sortConfig.key === column &&
            (sortConfig.direction === "ascending" ? (
              <ArrowUpwardIcon fontSize="small" />
            ) : (
              <ArrowDownwardIcon fontSize="small" />
            ))}
        </Box>
      </Box>
    </th>
  );

  const paginatedResults = sortedResults.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 1, mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            to="/dashboard/dashbordhome"
            style={{
              textDecoration: "none",
              color: theme.palette.primary.main,
            }}
          >
            Home
          </Link>
          <Typography color="textPrimary">
            Report Asset Physical Stock Take
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ mt: 3, overflowX: "auto" }}>
        <Paper elevation={3} sx={{ padding: "2%" }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Plant Code"
                fullWidth
                size="small"
                value={plantCode}
                onChange={handlePlantCodeChange}
              >
                {cName.plantCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Asset Code "
                fullWidth
                size="small"
                value={assetCode}
                onChange={(e) => setAssetCode(e.target.value)}
              >
                {cName.assetCodes.length === 0 ? (
                  <MenuItem value="" disabled>
                    No Asset Codes Available
                  </MenuItem>
                ) : (
                  cName.assetCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Category"
                fullWidth
                size="small"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                select
                label="Stock Status"
                name="StockStatus"
                fullWidth
                size="small"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                //   disabled={formMode === "initial"}
              >
                <MenuItem value="T">New Asset</MenuItem>
                <MenuItem value="Y">Old Asset</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                type="date"
                label="From Date"
                fullWidth
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                type="date"
                label="To Date"
                fullWidth
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end" mt={5} spacing={1}>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                onClick={handleReset}
                startIcon={<CloseIcon />}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : searchResults.length > 0 ? (
        <Box sx={{ mt: 3, overflowX: "auto" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchforasds}
            />
          </Box>
          <div className="table-container">
            <table className="responsive-table">
              <thead>
                <tr>
                <SortableHeader column="StockTakeNo" />
                  <SortableHeader column="Barcode" />
                  <SortableHeader column="PlantCode" />
                  <SortableHeader column="AssetCode" />
                  <SortableHeader column="Description" />
                  <SortableHeader column="Category" />
                  <SortableHeader column="Depreciation" />
                  {/* <SortableHeader column="DepreciationYear" /> */}
                  {/* <SortableHeader column="AfterDepreciation" /> */}
                  <SortableHeader column="TransBy" />
                  <SortableHeader column="TransDate" />
                  {/* <SortableHeader column="FromPlant" />
              <SortableHeader column="AssignStatus" />
              <SortableHeader column="De_AssignedBy" />
              <SortableHeader column="De_AssignedDate" /> */}
                </tr>
              </thead>
              <tbody>
                {paginatedResults.map((row) => (
                  <tr key={row.ID}>
                    <td data-label="StockTakeNo">{row.StockTakeNo}</td>
                    <td data-label="Barcode">{row.Barcode}</td>
                    <td data-label="Plant Code">{row.PlantCode}</td>
                    <td data-label="Asset Code">{row.AssetCode}</td>
                    <td data-label="Description">{row.Description}</td>
                    <td data-label="Category">{row.Category}</td>
                    <td data-label="Depreciation">{row.Depreciation}%</td>
                    {/* <td data-label="Depreciation Year">
                      {row.DepreciationYear}
                    </td> */}
                    {/* <td data-label="AfterDepreciation">
                      {row.AfterDepreciation}
                    </td> */}
                    <td data-label="Created By">{row.TransBy}</td>
                    {/* <td data-label="Cost">{row.Cost}</td>
                <td data-label="From Plant">{row.FromPlant}</td>
                <td data-label="De AssignedBy">{row.De_AssignedBy}</td>
                <td data-label="Assign Status">{row.AssignStatus}</td> */}
                    <td data-label="TransDate">
                      {new Date(row.TransDate).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination
              component="div"
              count={sortedResults.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
            {/* <Pagination
count={Math.ceil(sortedResults.length / rowsPerPage)}
page={page + 1}
onChange={(event, value) => setPage(value - 1)}
showFirstButton
showLastButton
/> */}
          </div>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={exportToExcel}
            >
              Export to Excel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Typography variant="body1">No data found</Typography>
        </Box>
      )}
    </Container>
  );
};

export default RepAssetPhStockTake;
